/**
 * Normalizer for account/order records from GET /api/clickup/accounts.
 *
 * These are ClickUp order tasks brokered by the backend. Field names and the
 * shape of `status` vary (ClickUp sends status as an object; the feed may
 * flatten it to a string), so — exactly as with schools and championships —
 * nothing in the UI couples to raw fields. Everything goes through
 * `normalizeAccount()`, which maps many candidate keys into one clean shape and
 * classifies the raw ClickUp status into a canonical Customer Journey stage.
 *
 * Read-only: the Customer Journey view never writes back. A write path exists
 * (POST /api/clickup/task-status) but is intentionally stubbed here.
 */

const props = (r) => (r && typeof r.properties === 'object' ? r.properties : {})

function field(record, keys) {
  const p = props(record)
  for (const k of keys) {
    if (record?.[k] !== undefined && record[k] !== null && record[k] !== '')
      return record[k]
    if (p?.[k] !== undefined && p[k] !== null && p[k] !== '') return p[k]
  }
  return undefined
}

/** ClickUp epoch-ms (number or numeric string) or ISO string → Date | null. */
function parseDate(value) {
  if (value == null || value === '') return null
  if (typeof value === 'number') return new Date(value)
  const s = String(value).trim()
  if (/^\d{10,}$/.test(s)) return new Date(Number(s)) // epoch ms
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Pull the human status text out of a ClickUp status (object or string). */
function rawStatusText(record) {
  const s = field(record, ['status', 'order_status', 'stage', 'state', 'phase'])
  if (s == null) return ''
  if (typeof s === 'string') return s
  if (typeof s === 'object') return String(s.status || s.name || s.label || '')
  return String(s)
}

/* ---- The canonical Customer Journey pipeline ----------------------- */

/**
 * The order's lifecycle, left to right. `tone` maps to a status hue token.
 * Coloring, funnel order, and filtering all key off these stage keys — never
 * off the raw ClickUp status string (which is inconsistent across lists).
 */
export const JOURNEY_STAGES = [
  { key: 'new', label: 'New', blurb: 'Order received', tone: 'info' },
  { key: 'design', label: 'Design', blurb: 'Artwork & proofing', tone: 'brass' },
  { key: 'approval', label: 'Approval', blurb: 'Awaiting customer sign-off', tone: 'warn' },
  { key: 'production', label: 'Production', blurb: 'In manufacturing', tone: 'brass' },
  { key: 'fulfillment', label: 'Fulfillment', blurb: 'Shipping & delivery', tone: 'info' },
  { key: 'complete', label: 'Complete', blurb: 'Delivered & closed', tone: 'ok' },
]

export const HOLD = 'hold'
export const OTHER = 'other'

// Extra buckets that live outside the linear funnel.
export const OFF_PIPELINE = [
  { key: HOLD, label: 'On hold', blurb: 'Blocked / needs attention', tone: 'danger' },
  { key: OTHER, label: 'Other', blurb: 'Unmapped status', tone: 'muted' },
]

export const STAGE_LABEL = Object.fromEntries(
  [...JOURNEY_STAGES, ...OFF_PIPELINE].map((s) => [s.key, s.label])
)
export const STAGE_TONE = Object.fromEntries(
  [...JOURNEY_STAGES, ...OFF_PIPELINE].map((s) => [s.key, s.tone])
)

// The order in which a status is tested; first hit wins, so the more specific
// / later-lifecycle checks come before generic ones (e.g. "awaiting approval"
// must beat "new", "in production" must beat a bare "in progress").
const STAGE_MATCHERS = [
  [HOLD, /\b(hold|on[-\s]?hold|blocked|stuck|issue|problem|cancel|canceled|cancelled|void|refund|revision needed|rejected)\b/i],
  ['complete', /\b(complete|completed|delivered|fulfilled|closed|done|finished|shipped & received|paid|reorder|archived)\b/i],
  ['fulfillment', /\b(ship|shipping|shipped|fulfillment|fulfilment|packing|packaging|out for delivery|in transit|dispatch|dispatched|ready to ship|ready for pickup)\b/i],
  ['production', /\b(production|manufactur|engrav|printing|printed|embroider|building|assembl|in progress|wip|making|fabric)\b/i],
  ['approval', /\b(approval|approve|awaiting|pending approval|customer review|sign[-\s]?off|proof sent|proof review|needs approval|client review)\b/i],
  ['design', /\b(design|artwork|art|proof|mockup|mock[-\s]?up|draft|rendering|render|layout|digitiz|setup)\b/i],
  ['new', /\b(new|intake|submitted|received|open|to ?do|backlog|quote|lead|order placed|not started)\b/i],
]

/** Classify a raw ClickUp status string into a canonical journey stage key. */
export function classifyStage(rawStatus) {
  const s = String(rawStatus || '').trim()
  if (!s) return OTHER
  for (const [key, re] of STAGE_MATCHERS) {
    if (re.test(s)) return key
  }
  return OTHER
}

/** A numeric progress ratio (0–1) for the funnel/progress bar on a card. */
export function stageProgress(stageKey) {
  const idx = JOURNEY_STAGES.findIndex((s) => s.key === stageKey)
  if (idx === -1) return null // hold / other are off-pipeline
  return (idx + 1) / JOURNEY_STAGES.length
}

/* ---- Normalization ------------------------------------------------- */

export function normalizeAccount(record) {
  if (!record || typeof record !== 'object') return null

  const id = field(record, ['id', 'task_id', 'taskId', 'hs_object_id', 'objectId'])
  const account =
    field(record, [
      'account',
      'account_name',
      'customer',
      'customer_name',
      'school',
      'school_name',
      'company',
      'client',
    ]) || ''
  const order =
    field(record, [
      'name',
      'order_name',
      'title',
      'task_name',
      'order',
      'order_number',
      'po',
    ]) || 'Untitled order'

  const rawStatus = rawStatusText(record)
  const stage = classifyStage(rawStatus)

  const dueRaw = field(record, [
    'due_date',
    'dueDate',
    'due',
    'ship_date',
    'target_date',
    'delivery_date',
  ])
  const updatedRaw = field(record, [
    'date_updated',
    'updated_at',
    'last_activity',
    'modified',
    'date_modified',
  ])
  const createdRaw = field(record, ['date_created', 'created_at', 'created', 'order_date'])

  /* ⚠️ THE ORDER FEED IS GROUPED BY ACCOUNT, NOT FLAT.
   *
   * `/api/clickup/accounts` returns
   *     { account, orderCount, counts, attention, owners:[{id,name,email}],
   *       phase, lastActivity, orders:[{ id, name, url, status, phase, … }] }
   *
   * Every lookup below was written for a FLAT ClickUp task — `assignee`,
   * `name`, `date_updated`. None of those keys exist on this shape, so the
   * field helper returned '' every time and the page rendered
   * **"Unassigned" and "Untitled order" on all 3,095 accounts** while the
   * response carried the rep's full name and the real order title.
   *
   * The screen was not missing data. It was reading the wrong shape, and
   * because the fallbacks degrade silently to a friendly placeholder, it
   * looked like an empty CRM rather than a mapping bug. Both shapes are
   * handled now; the grouped one wins because it is what production returns.
   */
  const orders = Array.isArray(record.orders) ? record.orders : []
  const primary = orders[0] || null

  const ownerList = Array.isArray(record.owners)
    ? record.owners.map((o) => (typeof o === 'string' ? o : String(o?.name || ''))).filter(Boolean)
    : []
  const owner =
    ownerList[0] ||
    field(record, ['assignee', 'owner', 'rep', 'sales_rep', 'account_owner']) ||
    ''

  const value = field(record, ['value', 'amount', 'order_value', 'total', 'price'])
  const url = field(record, ['url', 'clickup_url', 'link', 'permalink']) || primary?.url || ''

  return {
    id: id != null ? String(id) : (primary?.id ? String(primary.id) : `${account}-${order}`),
    account: String(account),
    // The grouped feed puts the real title on the order, not the account.
    order: String(orders.length ? (primary?.name || order) : order),
    orders,
    orderCount: Number(record.orderCount ?? orders.length) || orders.length,
    owners: ownerList,
    // Per-phase order counts, straight from the backend's own classification.
    counts: record.counts && typeof record.counts === 'object' ? record.counts : null,
    attention: Number(record.attention ?? 0) || 0,
    stage, // locally-derived stage — kept only as a FALLBACK, see below
    /* THE BACKEND'S PHASE, CARRIED THROUGH UNTOUCHED.
     *
     * This file derives `stage` from the raw status text with its own regex.
     * The backend ALSO classifies every order, from the list's configured
     * statuses, and reports `unmappedStatuses: []` — it maps all 3,408 of
     * them. Two independent classifiers over the same field is one too many,
     * and the one being used here was the weaker of the two.
     *
     * It showed: the page read "Approvals 0" while the feed held 163 orders
     * in Approvals. Not a rounding difference — 163 orders waiting on a
     * sign-off, invisible to the team that has to chase them.
     *
     * Flow of truth: the backend owns the phase, this layer displays it. The
     * local derivation stays only for a feed that predates the field.
     */
    phase: record.phase != null ? String(record.phase) : '',
    statusRaw: rawStatus || String(primary?.status || ''),
    dueDate: parseDate(dueRaw != null ? dueRaw : primary?.dueDate),
    dueRaw: dueRaw != null ? String(dueRaw) : String(primary?.dueDate ?? ''),
    // The grouped feed calls it `lastActivity`; a flat task calls it date_updated.
    updatedAt: parseDate(updatedRaw != null ? updatedRaw : record.lastActivity),
    createdAt: parseDate(createdRaw),
    owner: String(owner || ''),
    value: value != null && value !== '' ? Number(value) || value : null,
    url: String(url || ''),
    raw: record,
  }
}

export function normalizeAccounts(response) {
  const list = Array.isArray(response)
    ? response
    : response?.accounts ||
      response?.tasks ||
      response?.results ||
      response?.data ||
      response?.items ||
      response?.records ||
      []
  return list.map(normalizeAccount).filter(Boolean)
}

/* ---- Aggregation --------------------------------------------------- */

export function countByStage(accounts) {
  const counts = {}
  for (const a of accounts) counts[a.stage] = (counts[a.stage] || 0) + 1
  return counts
}

/** Overdue = has a due date in the past and isn't complete. */
export function isOverdue(account, now = new Date()) {
  if (!account.dueDate || account.stage === 'complete') return false
  return account.dueDate < now
}

/** Filter by stage (null = any) and a free-text query over account/order/owner. */
export function filterAccounts(accounts, { stage, query } = {}) {
  const q = String(query || '').trim().toLowerCase()
  return accounts.filter((a) => {
    if (stage && a.stage !== stage) return false
    if (q) {
      const hay = `${a.account} ${a.order} ${a.owner} ${a.statusRaw}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
}

/** Sort: attention first (overdue, then approval), then by due date, undated last. */
export function sortAccounts(accounts, now = new Date()) {
  const rank = (a) => {
    if (isOverdue(a, now)) return 0
    if (a.stage === 'hold') return 1
    if (a.stage === 'approval') return 2
    return 3
  }
  return [...accounts].sort((a, b) => {
    const r = rank(a) - rank(b)
    if (r !== 0) return r
    if (!a.dueDate && !b.dueDate) return 0
    if (!a.dueDate) return 1
    if (!b.dueDate) return -1
    return a.dueDate - b.dueDate
  })
}

export function formatDate(dateObj) {
  if (!dateObj) return 'No date'
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatRelative(dateObj, now = new Date()) {
  if (!dateObj) return ''
  const diff = Math.round((dateObj - now) / 86400000) // days
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  if (diff === -1) return 'yesterday'
  if (diff > 0) return `in ${diff} days`
  return `${Math.abs(diff)} days ago`
}
